import type { ReactNode } from "react"
import { v4 as uuid } from "uuid"

import type { CreateFilterConfig, SerializedFilter } from "../../filter"
import { Filter } from "../../filter"
import type {
	AnyFilterDefModifierConfig,
	FilterDefRenderProps,
	FilterModifiersOfFilterDef,
	FilterValueOfFilterDef,
	SerializedFilterValueOfFilterDef,
} from "./typings"

export abstract class BaseFilterDef<TValue, TFilterValue, TSerializedFilterValue = TFilterValue> {
	private _value: TValue | undefined

	abstract readonly id: string
	abstract readonly defaultFilterValue: TFilterValue | undefined
	abstract readonly defaultFilterModifier: keyof typeof this.modifiers
	abstract readonly modifiers: Record<
		string,
		AnyFilterDefModifierConfig<TValue, TFilterValue, TSerializedFilterValue>
	>

	abstract render(props: FilterDefRenderProps<BaseFilterDef<TValue, TFilterValue, TSerializedFilterValue>>): ReactNode

	createFilter(config?: CreateFilterConfig<typeof this>): Filter<typeof this> {
		return new Filter<typeof this>({
			id: config?.id ?? uuid(),
			filterDef: this,
			filterValue:
				config?.filterValue ?? (this.defaultFilterValue as FilterValueOfFilterDef<typeof this>) ?? undefined,
			filterModifier:
				config?.filterModifier ?? (this.defaultFilterModifier as FilterModifiersOfFilterDef<typeof this>),
		})
	}

	getFilterModifier = (modifier: keyof typeof this.modifiers) => {
		return this.modifiers[modifier]
	}

	getFilterModifiers = () => {
		return Object.entries(this.modifiers) as [
			keyof typeof this.modifiers,
			AnyFilterDefModifierConfig<TValue, TFilterValue, TSerializedFilterValue>,
		][]
	}

	serialize(filter: Filter<typeof this>): SerializedFilter<typeof this> {
		const filterValue = filter.getFilterValue()
		const modifierKey = filter.getFilterModifier()
		const modifierConfig = this.modifiers[modifierKey]

		let serializedValue: SerializedFilterValueOfFilterDef<typeof this> | undefined

		if (filterValue !== undefined && modifierConfig.isFilterValueValid(filterValue)) {
			serializedValue = modifierConfig.modifier.serialize(filterValue)
		} else {
			serializedValue = undefined
		}

		return {
			id: filter.id,
			filterDefId: this.id,
			filterValue: serializedValue,
			filterModifier: modifierKey,
		}
	}

	deserialize(serializedFilter: SerializedFilter<typeof this>): Filter<typeof this> {
		const modifierKey = serializedFilter.filterModifier
		const modifierConfig = this.modifiers[modifierKey]
		const serializedFilterValue = serializedFilter.filterValue

		let deserializedValue: FilterValueOfFilterDef<typeof this> | undefined = undefined

		if (serializedFilterValue !== undefined) {
			deserializedValue = modifierConfig.modifier.deserialize(serializedFilter.filterValue)
		}

		return new Filter<typeof this>({
			id: serializedFilter.id,
			filterDef: this,
			filterValue: deserializedValue,
			filterModifier: serializedFilter.filterModifier,
		})
	}
}
